/**
 * RendicionesGastosList — CHS Iter 313.j (feb-2026).
 *
 * Listado de rendiciones de gastos ("Rendición Caja Chica" / "Fondos por Rendir").
 * Solo accesible para usuarios que tienen un `medio_pago` categoria='rendicion'
 * cuya `persona_asociada` matchea su `user.nombre`. Gerencia / Contabilidad
 * pueden ver TODAS las rendiciones (para aprobar / rechazar).
 *
 * Estados: borrador | enviada_vb | aprobada | rechazada | integrada_alegra
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { TableSkeleton } from '../../lib/skeletons';
import {
  Plus, Eye, Pencil, Send, CheckCircle, XCircle, Trash2, RefreshCw, Wallet, Printer,
} from 'lucide-react';

const fmtCLP = (n) => `$ ${(n || 0).toLocaleString('es-CL')}`;
const fmtFecha = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = iso.substring(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

const ESTADO_META = {
  borrador:          { label: 'Borrador',      color: 'bg-gray-200 text-gray-700' },
  enviada_vb:        { label: 'Enviada a VB',  color: 'bg-amber-100 text-amber-800' },
  aprobada:          { label: 'Aprobada',      color: 'bg-blue-100 text-blue-800' },
  rechazada:         { label: 'Rechazada',     color: 'bg-red-100 text-red-800' },
  integrada_alegra:  { label: 'Contabilizada', color: 'bg-green-100 text-green-800' },
  modificada:        { label: 'Modificada',    color: 'bg-orange-100 text-orange-800' },
  anulada:           { label: 'Anulada',       color: 'bg-red-200 text-red-900' },
};

export const RendicionesGastosList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [fondo, setFondo] = useState(null);
  const [fondoError, setFondoError] = useState(null);
  const [verTodas, setVerTodas] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');

  const isGerencia = user?.rol === 'admin' || user?.rol === 'gerencia' || user?.rol === 'contabilidad';

  const loadFondo = useCallback(async () => {
    try {
      const r = await api.get('/rendiciones-gastos/mi-fondo');
      setFondo(r.data);
      setFondoError(null);
    } catch (err) {
      setFondo(null);
      setFondoError(err?.response?.data?.detail || 'No tienes un fondo por rendir asociado.');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (verTodas && isGerencia) params.incluir_todas = true;
      if (filtroEstado) params.estado = filtroEstado;
      const r = await api.get('/rendiciones-gastos', { params });
      setItems(r.data || []);
    } catch (err) {
      toast.error('Error al cargar rendiciones', { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [verTodas, filtroEstado, isGerencia]);

  useEffect(() => { loadFondo(); }, [loadFondo]);
  useEffect(() => { loadList(); }, [loadList]);

  const handleDelete = async (id, folio) => {
    if (!window.confirm(`¿Eliminar rendición ${folio}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/rendiciones-gastos/${id}`);
      toast.success('Rendición eliminada');
      loadList();
    } catch (err) {
      toast.error('Error al eliminar', { description: err?.response?.data?.detail });
    }
  };

  const handleEnviarVB = async (id, folio) => {
    if (!window.confirm(`¿Enviar rendición ${folio} a VB de Gerencia?`)) return;
    try {
      await api.post(`/rendiciones-gastos/${id}/enviar-vb`);
      toast.success('Rendición enviada a VB de Gerencia/Administrador');
      loadList();
    } catch (err) {
      toast.error('Error', { description: err?.response?.data?.detail });
    }
  };

  const handleAprobar = async (id, folio, total, estado) => {
    // Iter 313.j.39: mensaje distinto para atajo desde borrador
    const prompt = estado === 'borrador'
      ? `¿Autorizar directamente la rendición ${folio} por ${fmtCLP(total)}? (atajo admin — las OCs ya deben estar aprobadas)`
      : `¿Aprobar rendición ${folio} por ${fmtCLP(total)}?`;
    if (!window.confirm(prompt)) return;
    try {
      await api.post(`/rendiciones-gastos/${id}/aprobar`);
      // Iter 313.j.38 (Renzo, feb-2026): tras aprobar, ofrecer contabilizar
      // inmediatamente en Alegra (redirige al detalle con auto-abrir).
      if (window.confirm('Rendición autorizada. ¿Contabilizar ahora en Alegra?')) {
        navigate(`/compras/rendiciones-gastos/${id}?auto_contabilizar=1`);
        return;
      }
      toast.success('Rendición autorizada. Lista para integrar a Alegra.');
      loadList();
    } catch (err) {
      toast.error('Error', { description: err?.response?.data?.detail });
    }
  };

  const handleRechazar = async (id, folio) => {
    const motivo = window.prompt(`Motivo de rechazo para rendición ${folio}:`, '');
    if (!motivo || !motivo.trim()) return;
    try {
      await api.post(`/rendiciones-gastos/${id}/rechazar`, { motivo });
      toast.success('Rendición rechazada. El rendidor puede editarla.');
      loadList();
    } catch (err) {
      toast.error('Error', { description: err?.response?.data?.detail });
    }
  };

  const handleImprimirPdf = (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${base}/api/rendiciones-gastos/${id}/pdf`, '_blank');
  };

  // Sin fondo asociado y no es gerencia → mostrar bloqueo
  if (fondoError && !isGerencia) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="rendiciones-sin-fondo">
        <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-800 rounded-lg p-6 text-center">
          <Wallet className="w-12 h-12 text-amber-600 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-2">Sin fondo por rendir asociado</h2>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">{fondoError}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Si crees que esto es un error, contacta a Contabilidad para asociar tu nombre
            (<strong>{user?.nombre}</strong>) al medio de pago rendición correspondiente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="rendiciones-gastos-list-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Rendiciones de Gastos</h1>
          {fondo && (
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              <span>
                Tu fondo: <strong>{fondo.nombre}</strong>
                {fondo.alegra_bank_account_name && (
                  <span className="text-xs ml-2">
                    (Alegra: {fondo.alegra_bank_account_name})
                  </span>
                )}
              </span>
            </div>
          )}
          {isGerencia && !fondo && (
            <div className="text-xs text-gray-500 mt-1">
              No tienes un fondo propio asignado. Como Gerencia puedes crear rendiciones en nombre de cualquier funcionario.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Botón visible si tienes fondo propio O si eres Gerencia (puede crear en nombre de otro) */}
          {(fondo || isGerencia) && (
            <Button
              onClick={() => navigate('/compras/rendiciones-gastos/nueva')}
              data-testid="btn-nueva-rendicion"
            >
              <Plus className="w-4 h-4 mr-1" /> Nueva Rendición
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadList} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 text-sm bg-white dark:bg-slate-900 border rounded-md p-3">
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="border rounded-md px-2 py-1 text-sm bg-transparent"
          data-testid="filtro-estado"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        {isGerencia && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={verTodas}
              onChange={(e) => setVerTodas(e.target.checked)}
              data-testid="chk-ver-todas"
            />
            <span>Ver todas las rendiciones (no solo las mías)</span>
          </label>
        )}
        <div className="ml-auto text-xs text-gray-500">
          {items.length} rendición{items.length !== 1 && 'es'}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-slate-900 border rounded-md overflow-hidden">
        {loading ? (
          <TableSkeleton rows={5} cols={7} />
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No hay rendiciones {filtroEstado ? `en estado ${ESTADO_META[filtroEstado]?.label}` : ''}.
            {fondo && (
              <div className="mt-2 text-xs">
                Haz clic en <strong>Nueva Rendición</strong> para crear la primera.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 dark:bg-slate-800 border-b">
                <tr>
                  <th className="text-left py-2 px-3">Folio</th>
                  <th className="text-left py-2 px-3">Fecha</th>
                  <th className="text-left py-2 px-3">Rendidor</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-center py-2 px-3">Estado</th>
                  <th className="text-left py-2 px-3">Última act.</th>
                  <th className="text-center py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isOwner = r.rendidor?.user_email === user?.email;
                  const canEdit = (r.estado === 'borrador' || r.estado === 'rechazada') && (isOwner || isGerencia);
                  const canDelete = canEdit;
                  const canEnviarVB = r.estado === 'borrador' && isOwner;
                  // Iter 313.j.39 (Renzo): admin/gerencia también puede aprobar directo desde 'borrador'
                  // (el backend valida en el POST que todas las OCs estén aprobadas).
                  const canAprobar = (r.estado === 'enviada_vb' || r.estado === 'borrador') && isGerencia;
                  const canRechazar = r.estado === 'enviada_vb' && isGerencia;
                  const meta = ESTADO_META[r.estado] || { label: r.estado, color: 'bg-gray-100' };
                  return (
                    <tr key={r.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`row-rendicion-${r.folio}`}>
                      <td className="py-2 px-3 font-mono font-bold">{r.folio}</td>
                      <td className="py-2 px-3">{fmtFecha(r.fecha)}</td>
                      <td className="py-2 px-3">{r.rendidor?.nombre || '-'}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmtCLP(r.totales?.total_general)}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.color}`}>
                          {meta.label}
                        </span>
                        {r.estado === 'rechazada' && r.motivo_rechazo && (
                          <div className="text-[9px] text-red-600 mt-0.5 max-w-[180px] truncate" title={r.motivo_rechazo}>
                            {r.motivo_rechazo}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-500">{fmtFecha(r.updated_at || r.created_at)}</td>
                      <td className="py-2 px-3 text-center whitespace-nowrap">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => navigate(`/compras/rendiciones-gastos/${r.id}`)}
                          title="Ver detalle"
                          data-testid={`btn-ver-${r.folio}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleImprimirPdf(r.id)}
                          title="Imprimir PDF"
                          data-testid={`btn-pdf-${r.folio}`}
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => navigate(`/compras/rendiciones-gastos/${r.id}/editar`)}
                            title="Editar"
                            data-testid={`btn-editar-${r.folio}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canEnviarVB && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleEnviarVB(r.id, r.folio)}
                            title="Enviar a VB de Gerencia/Administrador"
                            className="text-blue-600"
                            data-testid={`btn-enviar-vb-${r.folio}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canAprobar && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleAprobar(r.id, r.folio, r.totales?.total_general, r.estado)}
                            title={r.estado === 'borrador' ? 'Autorizar (atajo admin, OCs ya aprobadas)' : 'Aprobar (VB Gerencia/Administrador)'}
                            className="text-green-600"
                            data-testid={`btn-aprobar-${r.folio}`}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canRechazar && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleRechazar(r.id, r.folio)}
                            title="Rechazar"
                            className="text-red-600"
                            data-testid={`btn-rechazar-${r.folio}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDelete(r.id, r.folio)}
                            title="Eliminar"
                            className="text-red-500"
                            data-testid={`btn-eliminar-${r.folio}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RendicionesGastosList;
